import React, { useEffect, useRef } from 'react'

import { signOut } from 'firebase/auth'
import { SnackbarProvider, useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'

import squareLogo from '@kolayyolla/common/images/logo_square_white.svg'
import wideLogo from '@kolayyolla/common/images/logo_white.svg'
import bg from '@kolayyolla/common/images/mainBG.jpg'
import { Logout } from '@kolayyolla/common/store/actions'
import { useScript } from '@kolayyolla/shared/customHooks'

import { ExitToAppOutlined, GpsFixedOutlined,  SearchOutlined } from '@mui/icons-material'
import { Button, Divider, Grid, IconButton, InputBase, Paper, Typography, useMediaQuery } from '@mui/material'
import { yellow } from '@mui/material/colors'
import { makeStyles } from '@mui/styles'

import { firebaseAuth } from '../../firebaseApp'

const useStyles = makeStyles((theme) => {
  const classList = {

    container: {
      minWidth: 350,
      height: '60vh',
      margin: 'auto',
      transition: 'all 1s ease 0s',
      textAlign: 'center',

      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: '#fff',
      position: 'relative'
    },
    topContainer: {
      position: 'absolute',
      width: '100%',
      top: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {

        top: theme.spacing(5)
      }
    },
    top: {
      display: 'flex',
      justifyContent: 'center',
      position: 'relative',
      alignItems: 'center'
    },
    middleContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    bottomContainer: {
      height: 500,
      padding: 24
    },
    wideLogo: {
      width: 250,
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    squareLogo: {
      width: 120,
      display: 'none',
      marginBottom: '2.5rem',
      marginTop: '2.5rem',
      [theme.breakpoints.only('xs')]: {
        display: 'initial'
      }
    },
    buttons: {
      display: 'flex',
      justifyContent: 'flex-end',
      margin: theme.spacing(1),
      gap: theme.spacing(1),
      position: 'absolute',
      right: 0,
      alignItems: 'center'
    },
    nameField: {
      color: '#fff'
    },
    loginButton: {
      fontWeight: 'bold',
      color: '#fff'
    },
    logoutButton: {
      color: '#fff'
    },
    findLocationBtn: {
      textTransform: 'none',
      backgroundColor: yellow[800],
      color: theme.palette.getContrastText(yellow[800])
    },
    registerButton: {
      minHeight: 40,
      width: 'auto',
      borderRadius: 1000,
      backgroundColor: 'white',
      color: 'black',
      fontWeight: 'bold'
    },
    header: {
      fontWeight: 'bold',
      margin: '0px 1.5rem 1rem 1.5rem',
      fontSize: 34,
      maxWidth: 600,
      color: '#fff',
      [theme.breakpoints.down('sm')]: {
        fontSize: 22,
        fontWeight: 'normal'
      }
    },
    link: {
      cursor: 'pointer',
      color: theme.palette.primary.main,
      marginLeft: theme.spacing(1)
    },

    searchBoxContainer: {
      borderRadius: theme.spacing(1),
      backgroundColor: 'white'

    },
    searchBox: {
      display: 'inline-flex',
      alignItems: 'center',
      width: '100%',
      height: 48,
      margin: '1rem',
      maxWidth: 700,
      padding: theme.spacing(1, 0.5, 1, 2),
      [theme.breakpoints.only('xs')]: {
        width: '90vw'
      }
    },
    input: {
      marginLeft: theme.spacing(1),
      flex: 1
    },

    bottomPart: {
      fontFamily: 'LeagueSpartan!important',

      '& h3': {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(1)

      }

    }

  }
  return classList
})

const LandingPageHeader = () => {
  const classes             = useStyles()
  const navigate            = useNavigate()
  const location            = useLocation()
  const dispatch            = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const inputRef            = useRef()
  const auth                = useSelector((state) => state.auth)
  const user                = useSelector((state) => state.user)

  const isSmallScreen = useMediaQuery((t) => t.breakpoints.down('sm'))

  const status = useScript(`https://maps.google.com/maps/api/js?key=${process.env.REACT_APP_API_KEY}&libraries=places`)
  useEffect(() => {
    if (status === 'ready') init()
  }, [status])

  useEffect(() => {
    console.log(location.hash)
    if (auth.isLoggedIn && user.data?.addresses.length === 0 && !user.isLoading && user.isLoaded && location.hash !== '#wl') {
      navigate('/newaddress')
    }
  }, [auth.isLoggedIn, user.data?.addresses, user.isLoading, location.hash])

  const init = () => {
    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current)
    autocomplete.setFields(['address_components', 'geometry', 'name', 'formatted_address'])

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()

      if (!place.geometry) {
        enqueueSnackbar(`Girilen adrese ait bir konum bulunamadı: '${place.name}'`, { variant: 'error' })
      } else {
        navigate('/newaddress', {
          state: { coord: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() } }

        })
      }
    })
  }

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        navigate('/newaddress', {
          state: { coord: { lat: position.coords.latitude, lng: position.coords.longitude } }

        })
      },
      (err) => enqueueSnackbar(err.code === 1 ? 'Error: Erişim Engellendi!' : err.code === 2 ? 'Error: Konum Bulunamadı!' : `Error :${err.message}`, { variant: 'error', anchorOrigin: { horizontal: 'left', vertical: 'bottom' } }),
      { timeout: 60000 })
    } else {
      enqueueSnackbar('Üzgünüz, tarayıcınız konum bilgisi paylaşımını desteklemiyor', { variant: 'error', anchorOrigin: { horizontal: 'left', vertical: 'bottom' } })
    }
  }

  const handleLogout = () => {
    signOut(firebaseAuth).then(() => {
      dispatch(Logout({ clearLocalStorage: true }))
    })
  }

  if (status !== 'ready') return null

  return (
    <Grid
    container
    className={classes.container}
    alignItems="center"
    style={{ backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.50) 70%), url(${bg})` }}
  >

    <Grid xl={12} xs={12} item className={classes.topContainer}>
      <div className={classes.top}>
        <img className={classes.wideLogo} src={wideLogo} alt="" />
        <div className={classes.buttons}>
          {auth.isLoggedIn
            ? (
              <>
                <Typography className={classes.nameField}>{auth.userData.displayName}</Typography>
                <IconButton className={classes.logoutButton} onClick={() => handleLogout()}>
                  <ExitToAppOutlined />
                </IconButton>
              </>
              )
            : (
              <>
                <Button variant="text" className={classes.loginButton} onClick={() => navigate('/login')}>Giriş Yap</Button>
                <Button variant="contained" className={classes.registerButton} onClick={() => navigate('/register')}>Kayıt ol</Button>
              </>
              )}
        </div>
      </div>
    </Grid>

    <Grid xl={12} xs={12} item className={classes.middleContainer}>
      <img className={classes.squareLogo} src={squareLogo} alt="" />
      <Typography variant="h5" className={classes.header}>Sipariş vermeninin kolay yolu, Hazırsan başlayalım... </Typography>

        <Paper className={classes.searchBox}>
        <SearchOutlined className={classes.icon} />
        <InputBase
          className={classes.input}
          placeholder="Adresinizi girin"
          inputProps={{ 'aria-label': 'search for address' }}
          inputRef={inputRef}
            />
          <Divider className={classes.divider} orientation="vertical" />
          {
            isSmallScreen ? <IconButton color="primary" aria-label="get-location" onClick={() => getLocation()}><GpsFixedOutlined /></IconButton> : (
              <Button  variant='contained' startIcon={<GpsFixedOutlined />} className={classes.findLocationBtn} aria-label="get location" onClick={() => getLocation()}>
              Konumumu Bul
            </Button>
            )
          }

          </Paper>

    </Grid>

  </Grid>
  )
}

export default function LandingPageHeaderNotistack ({ onChange }) {
  return (
    <SnackbarProvider maxSnack={3}>
      <LandingPageHeader onChange={onChange} />
    </SnackbarProvider>
  )
}
